import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    adminLoginRequest,
    verifyMfaRequest,
    resetMfaRequest,
    resetMfaTokenRequest,
    secureQRRequest,
    adminResetPasswordRequest,
    clearAdminAuthError,
    clearResetMfaToken,
    clearSecureQR,
} from '../store/actions/adminAuthActions';

export const useAdminAuth = () => {
    const dispatch = useDispatch();
    const adminAuth = useSelector(state => state.adminAuth);

    const login = useCallback(async (credentials) => {
        dispatch(adminLoginRequest(credentials.email, credentials.password));
        // La navigation est gérée par les useEffect dans les composants
    }, [dispatch]);

    const verifyMfa = useCallback(async (mfaCode) => {
        const currentSessionId = adminAuth.sessionId || localStorage.getItem('admin_session_id');
        if (!currentSessionId) {
            throw new Error('Session ID manquant');
        }

        dispatch(verifyMfaRequest(mfaCode, currentSessionId));
        // La navigation est gérée par les useEffect dans les composants
    }, [adminAuth.sessionId, dispatch]);

    const resetMfa = useCallback(async () => {
        const sessionId = adminAuth.sessionId || localStorage.getItem('admin_session_id');
        if (!sessionId) {
            throw new Error('Session ID manquant');
        }
        dispatch(resetMfaRequest(sessionId));
    }, [adminAuth.sessionId, dispatch]);

    const resetMfaToken = useCallback(async (email) => {
        dispatch(resetMfaTokenRequest(email));
    }, [dispatch]);

    const getSecureQR = useCallback(async (token, email) => {
        dispatch(secureQRRequest(token, email));
    }, [dispatch]);

    const resetPassword = useCallback(async (email) => {
        dispatch(adminResetPasswordRequest(email));
    }, [dispatch]);

    const clearError = useCallback(() => {
        dispatch(clearAdminAuthError());
    }, [dispatch]);

    const clearResetMfaTokenData = useCallback(() => {
        dispatch(clearResetMfaToken());
    }, [dispatch]);

    const clearSecureQRData = useCallback(() => {
        dispatch(clearSecureQR());
    }, [dispatch]);

    // Destructure adminAuth to exclude resetMfaToken and secureQR to avoid conflicts
    const {
        resetMfaToken: _resetMfaTokenState,
        secureQR: _secureQRState,
        ...restAdminAuth
    } = adminAuth;

    return {
        // Functions first
        login,
        verifyMfa,
        resetMfa,
        resetMfaToken, // Function - must be before spread
        getSecureQR,
        resetPassword,
        clearError,
        clearResetMfaTokenData,
        clearSecureQRData,
        // Expose state separately to avoid naming conflicts
        resetMfaTokenState: adminAuth.resetMfaToken,
        secureQRState: adminAuth.secureQR,
        // Spread rest of adminAuth state (excluding resetMfaToken and secureQR)
        ...restAdminAuth,
    };
};

